// Chart palette + theme hook (owned by the web:charts agent).
//
// Colours are colour-blind-safe (Okabe-Ito derived). Every packet class has a
// stable colour, but charts must NEVER rely on colour alone: PacketStrip adds
// hatching/icons and other charts add labels, so the encoding survives
// monochrome printing and colour-vision deficiencies.
//
// Themes are driven by CSS variables set on :root / [data-theme] by the pages
// agent. Canvas 2D and uPlot cannot resolve `var(--x)` on their own, so this
// module RESOLVES the variables to concrete colour strings via
// getComputedStyle, falling back to the built-in per-theme palette when a
// variable is absent (e.g. in tests or before the app stylesheet loads).

import { useEffect, useState } from 'react';
import type { PacketClass } from '@/engine/types';

export type ThemeName = 'light' | 'dark' | 'high-contrast';

/** Fully-resolved colour set used by canvas + uPlot rendering. */
export interface ChartPalette {
  /** Class colours. */
  ok: string;
  late: string;
  lostUp: string;
  lostDown: string;
  unknown: string;
  notSent: string;
  /** Chrome colours. */
  bg: string;
  panel: string;
  text: string;
  textMuted: string;
  axis: string;
  grid: string;
  threshold: string;
  accent: string;
  /** Severity ramp (loss %) for heatmaps / ladders. */
  sevOk: string;
  sevWarn: string;
  sevBad: string;
}

// Built-in fallbacks per theme. Kept in sync with the CSS-variable names below.
const LIGHT: ChartPalette = {
  ok: '#009e73',
  late: '#e69f00',
  lostUp: '#d55e00',
  lostDown: '#8256d0',
  unknown: '#7f7f7f',
  notSent: '#cfcfcf',
  bg: '#ffffff',
  panel: '#f6f7f9',
  text: '#1b1f24',
  textMuted: '#5b636e',
  axis: '#8a929c',
  grid: '#e6e9ee',
  threshold: '#111418',
  accent: '#0072b2',
  sevOk: '#009e73',
  sevWarn: '#e69f00',
  sevBad: '#d55e00',
};

const DARK: ChartPalette = {
  ok: '#33c39a',
  late: '#f2b134',
  lostUp: '#ff6a3d',
  lostDown: '#a884f0',
  unknown: '#9aa0a8',
  notSent: '#464b52',
  bg: '#14171c',
  panel: '#1b1f26',
  text: '#e8ebef',
  textMuted: '#9aa2ac',
  axis: '#5b636e',
  grid: '#272c34',
  threshold: '#e8ebef',
  accent: '#4aa8e0',
  sevOk: '#33c39a',
  sevWarn: '#f2b134',
  sevBad: '#ff6a3d',
};

const HIGH_CONTRAST: ChartPalette = {
  ok: '#00e676',
  late: '#ffd400',
  lostUp: '#ff2b2b',
  lostDown: '#c46bff',
  unknown: '#c0c0c0',
  notSent: '#555555',
  bg: '#000000',
  panel: '#000000',
  text: '#ffffff',
  textMuted: '#e0e0e0',
  axis: '#ffffff',
  grid: '#3a3a3a',
  threshold: '#ffffff',
  accent: '#00b0ff',
  sevOk: '#00e676',
  sevWarn: '#ffd400',
  sevBad: '#ff2b2b',
};

const BUILTIN: Record<ThemeName, ChartPalette> = {
  light: LIGHT,
  dark: DARK,
  'high-contrast': HIGH_CONTRAST,
};

/** Built-in palette for a theme, independent of the DOM. */
export function defaultPalette(theme: ThemeName): ChartPalette {
  return BUILTIN[theme];
}

// Maps ChartPalette keys to the CSS custom properties the pages agent exposes.
const CSS_VAR: Record<keyof ChartPalette, string> = {
  ok: '--plm-c-ok',
  late: '--plm-c-late',
  lostUp: '--plm-c-lostup',
  lostDown: '--plm-c-lostdown',
  unknown: '--plm-c-unknown',
  notSent: '--plm-c-notsent',
  bg: '--plm-bg',
  panel: '--plm-panel',
  text: '--plm-text',
  textMuted: '--plm-text-muted',
  axis: '--plm-axis',
  grid: '--plm-grid',
  threshold: '--plm-threshold',
  accent: '--plm-accent',
  sevOk: '--plm-sev-ok',
  sevWarn: '--plm-sev-warn',
  sevBad: '--plm-sev-bad',
};

function readComputed(theme: ThemeName): ChartPalette {
  const fallback = BUILTIN[theme];
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') {
    return fallback;
  }
  const cs = getComputedStyle(document.documentElement);
  const out = { ...fallback };
  (Object.keys(CSS_VAR) as (keyof ChartPalette)[]).forEach((key) => {
    const v = cs.getPropertyValue(CSS_VAR[key]).trim();
    if (v) out[key] = v;
  });
  return out;
}

/** Detect the active theme from the [data-theme] attribute or media query. */
export function detectTheme(): ThemeName {
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'light' || attr === 'dark' || attr === 'high-contrast') {
      return attr;
    }
  }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    if (window.matchMedia('(prefers-contrast: more)').matches) return 'high-contrast';
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  }
  return 'light';
}

/**
 * React hook: current theme name plus its fully-resolved palette. Re-resolves
 * when [data-theme] changes or when the OS colour-scheme / contrast preference
 * changes, so charts restyle live without a reload.
 */
export function useChartTheme(): { theme: ThemeName; palette: ChartPalette } {
  const [theme, setTheme] = useState<ThemeName>(() => detectTheme());
  const [palette, setPalette] = useState<ChartPalette>(() => readComputed(detectTheme()));

  useEffect(() => {
    const sync = () => {
      const t = detectTheme();
      setTheme(t);
      setPalette(readComputed(t));
    };
    sync();

    let observer: MutationObserver | undefined;
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(sync);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme', 'style', 'class'],
      });
    }

    const mqls: MediaQueryList[] = [];
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      mqls.push(window.matchMedia('(prefers-color-scheme: dark)'));
      mqls.push(window.matchMedia('(prefers-contrast: more)'));
      mqls.forEach((m) => m.addEventListener('change', sync));
    }

    return () => {
      observer?.disconnect();
      mqls.forEach((m) => m.removeEventListener('change', sync));
    };
  }, []);

  return { theme, palette };
}

/** Whether the viewer prefers reduced motion (charts skip animated transitions). */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Human-readable label for each packet class. */
export const PACKET_CLASS_LABEL: Record<PacketClass, string> = {
  ok: 'OK',
  late: 'Late',
  lostUp: 'Lost (upstream)',
  lostDown: 'Lost (downstream)',
  unknown: 'Lost (unknown)',
  notSent: 'Not sent',
};

/** Resolve the colour for a packet class from a palette. */
export function classColor(palette: ChartPalette, cls: PacketClass): string {
  return palette[cls];
}

/**
 * Severity colour for a loss percentage using the fixed 0 / 5 / 20 breakpoints
 * shared with the heatmap. Interpolates within each band for a smooth ramp.
 */
export function severityColor(palette: ChartPalette, lossPct: number): string {
  const p = Number.isFinite(lossPct) ? lossPct : 0;
  if (p <= 0) return palette.sevOk;
  if (p <= 5) return mix(palette.sevOk, palette.sevWarn, p / 5);
  if (p <= 20) return mix(palette.sevWarn, palette.sevBad, (p - 5) / 15);
  return palette.sevBad;
}

// --- small colour helpers -------------------------------------------------

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Linear blend of two colours; t in [0,1]. Falls back to `a` on parse failure. */
export function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return a;
  const k = Math.min(1, Math.max(0, t));
  const r = Math.round(ca.r + (cb.r - ca.r) * k);
  const g = Math.round(ca.g + (cb.g - ca.g) * k);
  const bl = Math.round(ca.b + (cb.b - ca.b) * k);
  return `rgb(${r}, ${g}, ${bl})`;
}

/** Same colour at a given alpha; used for fills under lines and grid bands. */
export function withAlpha(color: string, alpha: number): string {
  const c = parseHex(color);
  if (!c) return color;
  const a = Math.min(1, Math.max(0, alpha));
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;
}

/**
 * A colour-blind-safe categorical sequence for per-node series (used by the
 * sweep line charts). Derived from the palette so it tracks the theme.
 */
export function categoricalColors(palette: ChartPalette): string[] {
  return [
    palette.accent,
    palette.ok,
    palette.late,
    palette.lostDown,
    palette.lostUp,
    palette.unknown,
  ];
}

/** Pick a readable text colour (black/white) for a solid background colour. */
export function readableOn(bg: string): string {
  const c = parseHex(bg);
  if (!c) return '#000000';
  // Relative luminance (sRGB approximation).
  const lum = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  return lum > 0.55 ? '#111418' : '#ffffff';
}
